// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for creating new asset lifecycle events.
 * Provides functionality to record significant points in an asset's history,
 * such as maintenance, inspections, or status changes.
 */
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { successResponseSchema } from '@/api/schemas';
import { responseMetadataSchema } from '@/api/types';
import { DateTime } from 'luxon';

const planMultiMoveIntersectionSchema = z.object({
  // Null when the conflict is another proposed move in the same batch rather than a
  // stored event group
  aegId: z.string().nullable(),
  aegKey: z.string().nullable(),
  start: z.string(),
  end: z.string().nullable(),
});

export type PlanMultiMoveIntersection = z.infer<
  typeof planMultiMoveIntersectionSchema
>;

const planMultiMoveResponseItemSchema = z.object({
  id: z.string(),
  isValid: z.boolean(),
  intersection: planMultiMoveIntersectionSchema.nullable(),
});

/**
 * Schema for validating the API response when creating an event.
 * Transforms the response to provide a more intuitive structure with
 * the event data separated from metadata.
 */
const planMultiMoveResponseSchema = z.object({
  data: z.array(planMultiMoveResponseItemSchema),
  metadata: responseMetadataSchema,
});

type PlanMultiMoveResponse = z.infer<typeof planMultiMoveResponseSchema>;

interface PlanMultiMoveParameters {
  id: string;
  assetId: string;
  shipAt?: DateTime;
  deliverAt?: DateTime;
}

export async function planMultiMove(
  parameters: PlanMultiMoveParameters[]
): Promise<PlanMultiMoveResponse> {
  const url = '/v1/move-asset/plan';
  const response = await api.post(url, parameters);
  return parseResponse(planMultiMoveResponseSchema, response.data, {
    schemaName: 'PlanMultiMove',
    endpoint: url,
  });
}

interface MultiMoveSubmission {
  assetId: string;
  originId: string;
  destinationId: string;
  shipAt?: DateTime;
  deliverAt?: DateTime;
}

interface TransformedMultiMoveSubmission extends MultiMoveSubmission {
  shipAtTimeZone?: string;
  deliverAtTimeZone?: string;
}

/**
 * Executes a multi-move operation for assets based on the provided input data.
 *
 * @param {MultiMoveSubmission} parameters - The data required to execute the multi-move operation.
 * @return {Promise<boolean>} A promise that resolves to a boolean indicating whether the operation was successful.
 */
export async function executeMultiMove(
  parameters: MultiMoveSubmission[]
): Promise<boolean> {
  const response = await api.post(
    `/v1/move-asset/execute`,
    parameters.map((item) => {
      const shipAtTimezone = item.shipAt?.zone.name;
      const deliverAtTimezone = item.deliverAt?.zone.name;
      return {
        ...item,
        shipAt: item.shipAt?.toUTC(),
        deliverAt: item.deliverAt?.toUTC(),
        shipAtTimezone: shipAtTimezone,
        deliverAtTimezone: deliverAtTimezone,
      } as TransformedMultiMoveSubmission;
    })
  );
  const url = '/v1/move-asset/execute';
  const data = await parseResponse(successResponseSchema, response.data, {
    schemaName: 'ExecuteMultiMove',
    endpoint: url,
  });
  return data.success;
}
